/* eslint-disable import/no-named-as-default */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import classNames from 'classnames/bind';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import FontLoader from 'yoda-core-components/lib/helpers/FontLoader/FontLoader';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { selectCmPageInclusionList } from '../../selectors/CMPageSelector';
import {
    selectIsNative,
    selectRequestUrl,
    selectShouldRender,
    selectDeviceType,
    isPreview as selectIsPreview,
} from '../../selectors/ContextSelector';
import getExternalComponent from '../../externalize/Externalize';
import Header from '../YodaHeader/Header';
import SignInSliderConnectLazy from '../SignInSlider/SignInSliderConnectLazy';
import FindStoresLazy from '../FindStores/FindStoresLazy';
import Footer from '../YodaFooter/FooterLazy';
import DateTimePickerLazy from '../DateTimePicker/DateTimePickerLazy';
import BrightEdgeAutopilot from '../BrightEdgeAutopilot/BrightEdgeAutopilot';
import CMSwitchWidget from '../CMSwitchWidget/CMSwitchWidget';
import PLPSwitchWidget from '../PLPSwitchWidget/PLPSwitchWidget';
import AppBanner from '../AppBanner/AppBanner';
import ScrollRenderer from '../Renderers/ScrollRenderer';
import DevToolBarLazy from '../../helpers/DevToolBar/DevToolBarLazy';

import * as styles from './Layout.css';

const cx = classNames.bind(styles);

const Layout = (props) => {
    const [displayDevToolBar, setDisplayDevToolBar] = useState(false);
    const [rewardsPilotIndicator, setRewardsPilotIndicator] = useState(false);

    const defaults = {
        signInSliderDetails: {
            openSignInSlider: false,
            openCreateAccount: false,
            openLogoutSlider: false,
        },
    };
    const signInSliderDetails = useSelector(
        (state) => state.signInSliderDetails || defaults.signInSliderDetails
    );
    const findAStoreSliderProps = useSelector((state) => state.findAStoreSliderProps);
    const enableBrightEdge = useSelector(
        (state) => state?.context?.featureFlags?.enableBrightEdge || false
    );
    const isNative = useSelector((state) => selectIsNative(state));
    const requestUrl = useSelector((state) => selectRequestUrl(state));
    const showGlobalLoader = useSelector((state) => state.showGlobalLoader || false);
    const shouldRender = useSelector((state) => selectShouldRender(state));
    const deviceType = useSelector((state) => selectDeviceType(state));
    const isPreview = useSelector((state) => selectIsPreview(state));
    const showOverlay = useSelector((state) => state.showOverlay || false);
    const cmPageinclusionList = useSelector((state) => selectCmPageInclusionList(state));
    const enableCMSwitchWidget = useSelector(
        (state) => state.context?.featureFlags?.enableCMSwitchWidget || false
    );
    const enablePLPSwitchWidget = useSelector(
        (state) => state.context?.featureFlags?.enablePLPSwitchWidget || false
    );
    const plpSwitchWidgetInclusionList = useSelector(
        (state) => state.context?.preferences?.plpSwitchWidget?.plpSwitchWidgetInclusionList || [],
        shallowEqual
    );
    const enableAppBanner = useSelector(
        (state) => state?.context?.featureFlags?.appBanner || false
    );
    const productSearchFocus = useSelector((state) => state.productSearchFocus || '');

    const setDevToolBar = () => {
        try {
            const jcpTester = window.location.search.indexOf('jcptester=true') !== -1;
            const jcpTesterCookie = Cookies.load('jcptester') || '';
            if (jcpTester || jcpTesterCookie === 'true') {
                setDisplayDevToolBar(true);
            }
        } catch (error) {
            console.log('Error ', error);
        }
    };

    const setRewardsIndicator = () => {
        const rewardsPilot = Cookies.load('eligibleForRewardsPilot') || false;
        if (rewardsPilot) {
            setRewardsPilotIndicator(true);
        }
    };

    useEffect(() => {
        try {
            const fontsList = [
                { fontname: 'Open Sans', weight: '400', style: 'normal' },
                { fontname: 'Open Sans', weight: '700', style: 'normal' },
                { fontname: 'Open Sans', weight: '400', style: 'italic' },
            ];
            new FontLoader().callToLoadFonts(fontsList, styles);
        } catch (e) {
            // Log the error
        }
        setDevToolBar();
        setRewardsIndicator();
    }, []);

    const getHeaderComponent = () => {
        const { hideHeader, checkoutProps, rewardsErrorObj } = props;
        if (hideHeader) {
            return null;
        }
        const headerProps = {
            mountOnServer: true,
            checkoutProps,
            rewardsErrorObj,
        };
        const headerData = getExternalComponent('header', headerProps);
        // eslint-disable-next-line react/jsx-props-no-spreading
        return process.env.ENV === 'aws' ? headerData : <Header {...headerProps} />;
    };

    const getSignInSliderComponent = () => {
        const { mountSignInSlider } = signInSliderDetails || {};
        if (mountSignInSlider) {
            if (process.env.ENV === 'aws') {
                return getExternalComponent('signInSlider');
            }
            return <SignInSliderConnectLazy />;
        }
        return null;
    };

    const renderFindStoresComponent = () => {
        /* istanbul ignore else */
        if (process.env.ENV === 'aws') {
            return getExternalComponent('findStoresSlider', findAStoreSliderProps);
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <FindStoresLazy {...findAStoreSliderProps} />;
    };

    const footerContent = () => {
        const { hideFooter, hideSubscription, renderRecZone } = props;
        if (hideFooter) {
            return null;
        }
        const footerProps = {
            expandedFooter: true,
            hideSubscription,
            renderRecZone,
        };
        const footerData = getExternalComponent('footer', footerProps);
        // eslint-disable-next-line react/jsx-props-no-spreading
        return process.env.ENV === 'aws' ? footerData : <Footer {...footerProps} />;
    };

    const headerComponent = getHeaderComponent();
    const displayBrightEdgeAutopilot = enableBrightEdge && !isNative && requestUrl !== '/s';

    /**
     * MCYODA-20881 => This is to show custom loader based on cart and checkout requirement,
     */
    const showLoader = showGlobalLoader ? 'progress-active' : '';

    if (!shouldRender) {
        return (
            <>
                {headerComponent}
                <article
                    className={cx('contentContainer', 'containerWidth', props.layoutArticleClass)}
                >
                    {props.children}
                </article>
                {displayBrightEdgeAutopilot ? <BrightEdgeAutopilot /> : null}
            </>
        );
    }

    // If Fusion Application is enabled, the FeaturePermission will return the module loader & module names.

    /* For the issue MNPDPYODA-2467. In future have to work on make it responsive */
    const blurClass =
        props.showBlurBG && `${dt(['blur-sm', 'fixed', 'right-0', 'left-0', 'top-0'])} ${'blurBg'}`;
    const lockWidthCss =
        deviceType.isDesktop && dt(['smMd:w-full', 'smMd:min-w-[1024px]', 'smMd:overflow-auto']);
    const backgroundCss = !props.disableWhiteBG && dt(['bg-white']);
    const mainClass = `${dt(['relative'])} ${cx(lockWidthCss, backgroundCss, blurClass)}`;
    const previewClass = isPreview && dt(['pt-0']); // added 0 to avoid blank space in preview homepage
    let overlay = dt([
        'hidden',
        'fixed',
        'top-0',
        'left-0',
        'h-full',
        'w-full',
        'sm:z-[5]',
        'bg-overlay-60',
    ]);
    if (showOverlay || props.showBlurBG) {
        overlay = dt([
            'block',
            'fixed',
            'top-0',
            'left-0',
            'h-full',
            'w-full',
            'sm:z-[5]',
            'bg-overlay-60',
        ]);
    }
    const previewMainHeaderClass = isPreview && cx('previewHeaderMainBlock');

    const params = requestUrl;
    const paramsPageType = params?.slice(0, 2);

    return (
        <main
            id="mainContainerBlock"
            data-automation-id="mainContainerBlock"
            className={cx(mainClass, previewClass, previewMainHeaderClass, showLoader)}
        >
            {isPreview && <DateTimePickerLazy showDateTimePicker />}
            {isPreview && (
                <div
                    className={dt([
                        'sm:!z-[13]',
                        'flex',
                        'fixed',
                        'lg:left-[55%]',
                        'smOnly:left-[50%]',
                        'smOnly:top-1.5',
                        'sm:!top-[3px]',
                        'mdOnly:left-[60%]',
                    ])}
                >
                    {params?.includes('/d/') &&
                        cmPageinclusionList?.includes(params) &&
                        isPreview &&
                        enableCMSwitchWidget && <CMSwitchWidget />}
                    {!params?.includes('/d/') && isPreview && enableCMSwitchWidget && (
                        <CMSwitchWidget />
                    )}
                    {isPreview &&
                        enablePLPSwitchWidget &&
                        plpSwitchWidgetInclusionList?.includes(paramsPageType) && (
                            <PLPSwitchWidget />
                        )}
                </div>
            )}
            {(showOverlay || props.showBlurBG) && (
                <div
                    id={rewardsPilotIndicator ? 'eligible-for-rewards-pilot' : ''}
                    className={overlay}
                />
            )}
            {!isNative && enableAppBanner ? (
                <div className={dt(['!m-0'])}>
                    <AppBanner />
                </div>
            ) : null}
            {headerComponent}
            <article
                id="contentContainer"
                className={cx(
                    'contentContainer',
                    'containerWidth',
                    props.layoutArticleClass,
                    productSearchFocus
                )}
            >
                {props.children}
            </article>
            {getSignInSliderComponent()}
            {!isEmpty(findAStoreSliderProps) && renderFindStoresComponent()}
            {displayBrightEdgeAutopilot ? <BrightEdgeAutopilot /> : null}
            <ScrollRenderer height={400}>{!isNative && footerContent()}</ScrollRenderer>
            {/* {!this.props.hideFooter && <YodaFooter expandedFooter />} */}
            {displayDevToolBar && <DevToolBarLazy />}
        </main>
    );
};

Layout.defaultProps = {
    hideHeader: false,
    checkoutProps: {},
    rewardsErrorObj: {},
    hideFooter: false,
    hideSubscription: false,
    renderRecZone: noop,
    layoutArticleClass: '',
    showBlurBG: false,
    disableWhiteBG: null,
};

Layout.propTypes = {
    children: PropTypes.element.isRequired,
    hideHeader: PropTypes.bool,
    checkoutProps: PropTypes.object,
    rewardsErrorObj: PropTypes.object,
    hideFooter: PropTypes.bool,
    hideSubscription: PropTypes.bool,
    renderRecZone: PropTypes.func,
    layoutArticleClass: PropTypes.string,
    showBlurBG: PropTypes.bool,
    disableWhiteBG: PropTypes.bool,
};

export default Layout;
